import { Injectable, inject, signal } from '@angular/core';
import { InterpolatableTranslationObject, TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { lastValueFrom } from 'rxjs';
import { primeNgTranslations } from '../i18n/primeng-translations';

export type SupportedLanguage = 'en' | 'it';

// localStorage key holding the active language. Also read at bootstrap by the
// LOCALE_ID factory in app.config.ts, so both must stay in sync.
export const LANGUAGE_STORAGE_KEY = 'app-language';
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Reads the persisted language without any Angular DI, so it can be used both by
 * the LOCALE_ID factory (at bootstrap, before services exist) and by this
 * service. Falls back to the default when nothing valid is stored.
 */
export function readPersistedLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'it') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode / SSR); fall back below.
  }
  return DEFAULT_LANGUAGE;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly translateService = inject(TranslateService);
  private readonly primeng = inject(PrimeNG);

  // Signal for current language
  readonly currentLanguage = signal<SupportedLanguage>(DEFAULT_LANGUAGE);

  // List of supported languages
  readonly supportedLanguages: SupportedLanguage[] = ['en', 'it'];

  async initializeLanguage(): Promise<InterpolatableTranslationObject> {
    // Priority logic:
    // 1. User preference from API (to be implemented if needed)
    // 2. localStorage
    // 3. Browser language
    // 4. Default fallback

    const savedLanguage = this.getLanguageFromStorage();
    const browserLanguage = this.getBrowserLanguage();

    const languageToUse = savedLanguage || browserLanguage || DEFAULT_LANGUAGE;

    // Reactive layers (ngx-translate strings + PrimeNG datepicker/filter labels)
    // are applied here without a reload. Angular's LOCALE_ID for pipes is fixed
    // per injector at bootstrap by the app.config.ts factory reading the SAME
    // persisted value, so no reload is needed on first load.
    return this.applyLanguage(languageToUse);
  }

  async setLanguage(language: SupportedLanguage): Promise<InterpolatableTranslationObject> {
    if (!this.supportedLanguages.includes(language)) {
      console.warn(
        `Language '${language}' is not supported. Falling back to '${DEFAULT_LANGUAGE}'.`
      );
      language = DEFAULT_LANGUAGE;
    }

    const previous = this.currentLanguage();
    const result = await this.applyLanguage(language);

    // LOCALE_ID is static per injector: once Angular is bootstrapped, changing
    // the language does NOT re-localize date/currency/decimal pipes on its own.
    // ngx-translate and PrimeNG update reactively above; to bring the Angular
    // locale in line we reload the app so the LOCALE_ID factory re-reads the
    // now-persisted language and pipes reinitialize with correct separators.
    if (previous !== language) {
      this.reloadApp();
    }

    return result;
  }

  /**
   * Applies a language across every i18n layer that CAN update without a reload:
   * ngx-translate strings and the PrimeNG translation (datepicker month/day
   * names, first day of week, filter/paginator labels). Persists the choice so
   * the next bootstrap picks it up for LOCALE_ID.
   */
  private async applyLanguage(
    language: SupportedLanguage
  ): Promise<InterpolatableTranslationObject> {
    const result = await lastValueFrom(this.translateService.use(language));
    this.primeng.setTranslation(primeNgTranslations[language]);
    this.currentLanguage.set(language);
    this.saveLanguageToStorage(language);
    return result;
  }

  private reloadApp(): void {
    try {
      window.location.reload();
    } catch (error) {
      console.warn('Unable to reload the app after language change:', error);
    }
  }

  private getLanguageFromStorage(): SupportedLanguage | null {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && this.supportedLanguages.includes(stored as SupportedLanguage)) {
        return stored as SupportedLanguage;
      }
    } catch (error) {
      console.warn('Unable to read language from localStorage:', error);
    }
    return null;
  }

  private saveLanguageToStorage(language: SupportedLanguage): void {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch (error) {
      console.warn('Unable to save language to localStorage:', error);
    }
  }

  private getBrowserLanguage(): SupportedLanguage | null {
    try {
      const browserLang = navigator.language.split('-')[0];
      return this.supportedLanguages.includes(browserLang as SupportedLanguage)
        ? (browserLang as SupportedLanguage)
        : null;
    } catch (error) {
      console.warn('Unable to detect browser language:', error);
      return null;
    }
  }
}
